import { toInteger } from 'lodash';
import { createCanvas, parseStrokeDasharray } from '@/utils/file-kit';
import { getFontFamilyWithFallback, getPlainTextFontByPretext, getPlainTextLayoutByPretext } from '@/utils/text-kit';
import { DEFAULT_FONT_FAMILY, type CellStyle, type ITableLayer } from '@/version-types';
import {
    DEFAULT_TABLE_CELL__BACKGROUND_COLOR,
    DEFAULT_TABLE_CELL__LINE_HEIGHT,
    DEFAULT_TABLE_CELL__TEXT_ALIGN,
    DEFAULT_TABLE_CELL__TEXT_COLOR,
    DEFAULT_TABLE_CELL__TEXT_FONT_SIZE,
    DEFAULT_TABLE_CELL__VERTICAL_ALIGN,
    TABLE_CELL_HORIZONTAL_PADDING,
} from '../default-configs';
import { getCellStyleForRender, getTableBorderLines, getTableCellRects, type TableCellRect } from '../utils/geometry';
import {
    getTableTextDecorationLineWidth,
    getTableTextLineThroughY,
    getTableTextLineVerticalPosition,
    getTableTextStartY,
    getTableTextUnderlineY,
} from '../utils/text-vertical-position';

/** 表格 Canvas 渲染选项。 */
interface TableCanvasRenderOptions {
    /** 是否把图层透明度写入像素；交互资源保持 false，由舞台统一处理透明度。 */
    globalAlpha?: boolean;
    /** 输出像素相对图层逻辑尺寸的缩放倍数。 */
    scale?: number;
    /** 编辑态仅隐藏指定单元格文字，背景和边框继续由 Canvas 绘制。 */
    suppressedCellId?: string | null;
}

/** 表格边框样式到基础虚线模板的映射。 */
const BORDER_DASH_MAP = {
    'line-0': '',
    'line-1': '2,1',
    'line-2': '1,1',
    'line-3': '2.5,0.5,0.5,0.5',
    'line-4': '2.5,0.5,0.5,0.5,0.5,0.5',
};
const getRenderFontFamily = (style: CellStyle, fontFamilyMap: Record<string, string>) => {
    const fontFamily = style.fontFamily ?? DEFAULT_FONT_FAMILY().name;
    const subsetFontFamily = fontFamilyMap[fontFamily];

    return getFontFamilyWithFallback(fontFamily, subsetFontFamily);
};

const getCanvasFont = (style: CellStyle, fontFamilyMap: Record<string, string>) =>
    getPlainTextFontByPretext({
        fontFamily: getRenderFontFamily(style, fontFamilyMap),
        fontSize: style.fontSize ?? DEFAULT_TABLE_CELL__TEXT_FONT_SIZE,
        bold: style.bold,
        italic: style.italic,
    });

const getTextX = (rect: TableCellRect, textAlign: CellStyle['textAlign'], lineWidth: number) => {
    switch (textAlign ?? DEFAULT_TABLE_CELL__TEXT_ALIGN) {
        case 'justify':
        case 'left':
            return rect.left + TABLE_CELL_HORIZONTAL_PADDING;
        case 'right':
            return rect.left + rect.width - TABLE_CELL_HORIZONTAL_PADDING - lineWidth;
        case 'center':
        default:
            return rect.left + (rect.width - lineWidth) / 2;
    }
};

const drawTextDecoration = ({
    ctx,
    line,
    lineWidth,
    textX,
    baselineY,
    style,
}: {
    ctx: CanvasRenderingContext2D;
    line: string;
    lineWidth: number;
    textX: number;
    baselineY: number;
    style: CellStyle;
}) => {
    if (!line || (!style.underline && !style.lineThrough)) return;

    const fontSize = style.fontSize ?? DEFAULT_TABLE_CELL__TEXT_FONT_SIZE;
    const startX = textX;
    const endX = textX + lineWidth;

    ctx.save();
    ctx.strokeStyle = style.color ?? DEFAULT_TABLE_CELL__TEXT_COLOR;
    ctx.lineWidth = getTableTextDecorationLineWidth(fontSize);
    if (style.underline) {
        const underlineY = getTableTextUnderlineY(baselineY, fontSize);
        ctx.beginPath();
        ctx.moveTo(startX, underlineY);
        ctx.lineTo(endX, underlineY);
        ctx.stroke();
    }
    if (style.lineThrough) {
        const lineThroughY = getTableTextLineThroughY(baselineY, fontSize);
        ctx.beginPath();
        ctx.moveTo(startX, lineThroughY);
        ctx.lineTo(endX, lineThroughY);
        ctx.stroke();
    }
    ctx.restore();
};
const drawCellText = (
    ctx: CanvasRenderingContext2D,
    rect: TableCellRect,
    style: CellStyle,
    fontFamilyMap: Record<string, string>,
) => {
    const text = rect.cell.content;
    if (!text) return;

    const fontSize = style.fontSize ?? DEFAULT_TABLE_CELL__TEXT_FONT_SIZE;
    const lineHeight = fontSize * DEFAULT_TABLE_CELL__LINE_HEIGHT;
    const contentWidth = Math.max(1, rect.width - TABLE_CELL_HORIZONTAL_PADDING * 2);
    const layout = getPlainTextLayoutByPretext(text, {
        fontFamily: getRenderFontFamily(style, fontFamilyMap),
        fontSize,
        bold: style.bold,
        italic: style.italic,
        lineHeightPx: lineHeight,
        width: contentWidth,
        disableAutoWrap: style.disableAutoWrap,
        wordBreak: 'break-all',
    });

    ctx.save();
    ctx.beginPath();
    ctx.rect(rect.left, rect.top, rect.width, rect.height);
    ctx.clip();
    ctx.font = getCanvasFont(style, fontFamilyMap);
    ctx.fillStyle = style.color ?? DEFAULT_TABLE_CELL__TEXT_COLOR;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';

    const startY = getTableTextStartY(rect, style.verticalAlign ?? DEFAULT_TABLE_CELL__VERTICAL_ALIGN, layout.height);
    const fontMetrics = ctx.measureText(text);

    layout.lines.forEach((line, index) => {
        const { baselineY } = getTableTextLineVerticalPosition(startY + index * lineHeight, lineHeight, fontMetrics);
        const textX = getTextX(rect, style.textAlign, line.width);
        if (line.text) {
            ctx.fillText(line.text, textX, baselineY);
        }
        drawTextDecoration({
            ctx,
            line: line.text,
            lineWidth: line.width,
            textX,
            baselineY,
            style,
        });
    });
    ctx.restore();
};
const getCanvasLineDash = (style: keyof typeof BORDER_DASH_MAP, borderWidth: number) => {
    return (parseStrokeDasharray(BORDER_DASH_MAP[style], borderWidth * 2, false) || []) as number[];
};

const drawTable = (ctx: CanvasRenderingContext2D, layer: ITableLayer, suppressedCellId?: string | null) => {
    const fontFamilyMap = layer.__runtime?.fontFamilyMap ?? {};
    const cellRects = getTableCellRects(layer);

    cellRects.forEach((rect) => {
        const style = getCellStyleForRender(rect.cell, layer.data);
        ctx.fillStyle = style.backgroundColor ?? DEFAULT_TABLE_CELL__BACKGROUND_COLOR;
        ctx.fillRect(rect.left, rect.top, rect.width, rect.height);
    });

    cellRects.forEach((rect) => {
        if (rect.id === suppressedCellId) return;

        const style = getCellStyleForRender(rect.cell, layer.data);
        drawCellText(ctx, rect, style, fontFamilyMap);
    });

    getTableBorderLines(layer).forEach(({ border, startX, startY, endX, endY }) => {
        if (border.width <= 0) return;

        ctx.save();
        ctx.beginPath();
        ctx.strokeStyle = border.color;
        ctx.lineWidth = border.width;
        ctx.setLineDash(getCanvasLineDash(border.style, border.width));
        ctx.moveTo(startX, startY);
        ctx.lineTo(endX, endY);
        ctx.stroke();
        ctx.restore();
    });
};

/** 将表格数据同步绘制为 Canvas，供编辑提交时无缝替换交互资源。 */
export const renderLayerContentToCanvasSync = (
    layer: ITableLayer,
    options: TableCanvasRenderOptions = {},
): HTMLCanvasElement => {
    const { globalAlpha = false, scale = 1, suppressedCellId } = options;
    const width = Math.max(1, toInteger(layer.width * scale));
    const height = Math.max(1, toInteger(layer.height * scale));
    const { canvas, ctx } = createCanvas(width, height);

    ctx.save();
    ctx.scale(scale, scale);
    ctx.globalAlpha = globalAlpha ? layer.opacity : 1;
    drawTable(ctx, layer, suppressedCellId);
    ctx.restore();

    return canvas;
};

/** 将表格数据直接绘制为 Canvas，不依赖编辑态 DOM。 */
export const renderLayerContentToCanvas = async (
    layer: ITableLayer,
    options: TableCanvasRenderOptions = {},
): Promise<HTMLCanvasElement> => renderLayerContentToCanvasSync(layer, options);
